import React, { useState } from 'react';
import Navigation from './components/Navigation.jsx';
import Hero from './components/Hero.jsx';
import Problem from './components/Problem.jsx';
import Solution from './components/Solution.jsx';
import HowItWorks from './components/HowItWorks.jsx';
import Pricing from './components/Pricing.jsx';
import FAQ from './components/FAQ.jsx';
import TrustLegal from './components/TrustLegal.jsx';
import Footer from './components/Footer.jsx';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './components/ui/sheet';

function App() {
  const [showTrustLegalModal, setShowTrustLegalModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showCookieModal, setShowCookieModal] = useState(false);

  const handleComplianceClick = () => {
    setShowTrustLegalModal(true);
  };

  const handlePrivacyClick = () => {
    setShowPrivacyModal(true);
  };

  const handleTermsClick = () => {
    setShowTermsModal(true);
  };

  const handleCookieClick = () => {
    setShowCookieModal(true);
  };

  return (
    <div className="App">
      <Navigation />
      <div>
        <Hero />
        <Problem />
        <Solution />
       {/* <HowItWorks /> */}
        <Pricing />
        {/* <FAQ /> */}
        <Footer 
          onComplianceClick={handleComplianceClick}
          onPrivacyClick={handlePrivacyClick}
          onTermsClick={handleTermsClick}
          onCookieClick={handleCookieClick}
        />
      </div>
      
      <Sheet open={showTrustLegalModal} onOpenChange={setShowTrustLegalModal}>
        <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-2xl font-bold font-sans">Compliance & Trust</SheetTitle>
          </SheetHeader>
          <div className="mt-6">
            <TrustLegal />
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={showPrivacyModal} onOpenChange={setShowPrivacyModal}>
        <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-2xl font-bold font-sans">Privacy Policy</SheetTitle>
          </SheetHeader>
          <div className="mt-6">
            <div className="prose max-w-none">
              <h3 className="text-lg font-semibold mb-4">1. Information We Collect</h3>
              <p className="mb-4">At CallSafe, we are committed to protecting your privacy. We collect information necessary to provide our AI-powered scam prevention training services.</p>
              
              <h4 className="font-semibold mb-2">Personal Information</h4>
              <p className="mb-4">When you register for our services, we collect your name, email address, phone number, and payment information. This information is essential for account creation and service delivery.</p>
              
              <h4 className="font-semibold mb-2">Training Data</h4>
              <p className="mb-4">We collect anonymized training session data including call simulations, response patterns, and learning progress. This data helps us improve our AI models and personalize your training experience.</p>
              
              <h4 className="font-semibold mb-2">Usage Analytics</h4>
              <p className="mb-4">We automatically collect information about how you interact with our platform, including pages visited, time spent, and features used. This helps us improve user experience.</p>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">2. How We Use Your Information</h3>
              <p className="mb-4">We use your information to provide, maintain, and improve our services in the following ways:</p>
              
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Deliver personalized AI training sessions</li>
                <li className="mb-2">Process payments and manage subscriptions</li>
                <li className="mb-2">Communicate with you about your account and services</li>
                <li className="mb-2">Analyze usage patterns to improve our platform</li>
                <li className="mb-2">Ensure security and prevent fraud</li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">3. Information Sharing</h3>
              <p className="mb-4">We do not sell, trade, or otherwise transfer your personal information to third parties without your explicit consent, except in the following circumstances:</p>
              
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Service providers who assist in operating our platform (payment processors, cloud services)</li>
                <li className="mb-2">Legal requirements when compelled by law or court order</li>
                <li className="mb-2">Business transfers (mergers, acquisitions) with your consent</li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">4. Data Security</h3>
              <p className="mb-4">We implement industry-standard security measures including:</p>
              
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">End-to-end encryption for sensitive data</li>
                <li className="mb-2">Regular security audits and penetration testing</li>
                <li className="mb-2">Limited employee access to personal information</li>
                <li className="mb-2">Secure data storage with redundant backups</li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">5. Your Rights</h3>
              <p className="mb-4">You have the right to:</p>
              
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Access your personal information</li>
                <li className="mb-2">Correct inaccurate information</li>
                <li className="mb-2">Delete your account and associated data</li>
                <li className="mb-2">Opt out of marketing communications</li>
                <li className="mb-2">Request data portability</li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">6. Contact Us</h3>
              <p>If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us at:</p>
              <p className="mb-2">Email: privacy@callsafe.training</p>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={showTermsModal} onOpenChange={setShowTermsModal}>
        <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-2xl font-bold font-sans">Terms of Service</SheetTitle>
          </SheetHeader>
          <div className="mt-6">
            <div className="prose max-w-none">
              <h3 className="text-lg font-semibold mb-4">1. Acceptance of Terms</h3>
              <p className="mb-4">By accessing and using CallSafe's AI-powered scam prevention training services, you accept and agree to be bound by the terms and provision of this agreement. These Terms of Service govern your use of our platform and services.</p>
              
              <p className="mb-4">If you do not agree to abide by these terms, please do not use our services. We reserve the right to update these terms at any time, and continued use of our services constitutes acceptance of any changes.</p>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">2. Service Description</h3>
              <p className="mb-4">CallSafe provides AI-driven training services designed to help individuals, particularly seniors, recognize and avoid phone scams. Our services include:</p>
              
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Simulated scam call training sessions</li>
                <li className="mb-2">Personalized learning recommendations</li>
                <li className="mb-2">Progress tracking and analytics</li>
                <li className="mb-2">Educational resources and support</li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">3. User Responsibilities</h3>
              <p className="mb-4">As a user of CallSafe, you agree to:</p>
              
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Provide accurate and truthful information when registering</li>
                <li className="mb-2">Use the service for personal, non-commercial purposes only</li>
                <li className="mb-2">Not attempt to reverse engineer or copy our AI technology</li>
                <li className="mb-2">Not share your account credentials with others</li>
                <li className="mb-2">Respect the privacy and rights of other users</li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">4. Subscription and Payment</h3>
              <p className="mb-4">CallSafe offers subscription-based services with the following terms:</p>
              
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Monthly and annual subscription options available</li>
                <li className="mb-2">Payments processed securely through third-party providers</li>
                <li className="mb-2">Subscriptions auto-renew unless cancelled</li>
                <li className="mb-2">No refunds for partial months of service</li>
                <li className="mb-2">Prices subject to change with 30-day notice</li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">5. Intellectual Property</h3>
              <p className="mb-4">All content, technology, and intellectual property associated with CallSafe remains the exclusive property of CallSafe Training, Inc. This includes but is not limited to:</p>
              
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">AI algorithms and training models</li>
                <li className="mb-2">Educational content and course materials</li>
                <li className="mb-2">Software code and user interface</li>
                <li className="mb-2">Brand names, logos, and trademarks</li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">6. Limitation of Liability</h3>
              <p className="mb-4">While CallSafe strives to provide effective training, we cannot guarantee prevention of all scam attempts. Our liability is limited as follows:</p>
              
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">CallSafe is not liable for financial losses from scams</li>
                <li className="mb-2">Our services are educational, not financial protection</li>
                <li className="mb-2">Maximum liability equals subscription fees paid</li>
                <li className="mb-2">We disclaim all warranties except as required by law</li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">7. Termination</h3>
              <p className="mb-4">We reserve the right to terminate or suspend accounts for:</p>
              
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Violation of these terms of service</li>
                <li className="mb-2">Fraudulent or abusive behavior</li>
                <li className="mb-2">Non-payment of subscription fees</li>
                <li className="mb-2">Any activity that harms other users or our service</li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">8. Contact Information</h3>
              <p>For questions about these Terms of Service, please contact us at:</p>
              <p className="mb-2">Email: legal@callsafe.training</p>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={showCookieModal} onOpenChange={setShowCookieModal}>
        <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-2xl font-bold font-sans">Cookie Policy</SheetTitle>
          </SheetHeader>
          <div className="mt-6">
            <div className="prose max-w-none">
              <h3 className="text-lg font-semibold mb-4">1. What Are Cookies</h3>
              <p className="mb-4">Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit our website. They contain information that helps us enhance your experience and provide personalized services.</p>
              
              <p className="mb-4">At CallSafe, we use cookies to improve our AI training platform, remember your preferences, and analyze how users interact with our services to better protect seniors from scams.</p>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">2. How We Use Cookies</h3>
              <p className="mb-4">We use cookies for several important purposes:</p>
              
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Remember your login status and account preferences</li>
                <li className="mb-2">Track your training progress and personalize learning paths</li>
                <li className="mb-2">Analyze website usage to improve user experience</li>
                <li className="mb-2">Ensure security and prevent fraudulent activities</li>
                <li className="mb-2">Provide customer support and troubleshooting</li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">3. Types of Cookies We Use</h3>
              
              <h4 className="font-semibold mb-2">Essential Cookies</h4>
              <p className="mb-4">These cookies are necessary for our website to function properly. They enable basic functions like page navigation, access to secure areas, and authentication. Without these cookies, our services cannot be provided.</p>
              
              <h4 className="font-semibold mb-2">Performance Cookies</h4>
              <p className="mb-4">These cookies collect information about how visitors use our website, such as which pages are visited most often and if users receive error messages. This helps us improve the performance of our site.</p>
              
              <h4 className="font-semibold mb-2">Functional Cookies</h4>
              <p className="mb-4">These cookies allow our website to remember choices you make and provide enhanced features. They may remember your preferences, training settings, and personalization options.</p>
              
              <h4 className="font-semibold mb-2">Analytics Cookies</h4>
              <p className="mb-4">These cookies help us understand how our website is being used, how effective our marketing campaigns are, and how to improve user experience. The data collected is anonymized and aggregated.</p>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">4. Third-Party Cookies</h3>
              <p className="mb-4">We work with trusted third-party service providers who may place cookies on your device:</p>
              
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Payment processors for secure transactions</li>
                <li className="mb-2">Analytics providers for website performance tracking</li>
                <li className="mb-2">Customer support platforms for service assistance</li>
                <li className="mb-2">Cloud hosting providers for reliable service delivery</li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">5. Managing Your Cookie Preferences</h3>
              <p className="mb-4">You have several options for managing cookies:</p>
              
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Browser settings to accept, reject, or delete cookies</li>
                <li className="mb-2">Cookie consent banner on our website for preference selection</li>
                <li className="mb-2">Regular clearing of cookies from your device</li>
                <li className="mb-2">Using private/incognito browsing modes</li>
              </ul>
              
              <p className="mb-4">Please note that disabling essential cookies may affect your ability to use certain features of our training platform.</p>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">6. Cookie Duration</h3>
              <p className="mb-4">Different cookies have different lifespans:</p>
              
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Session cookies expire when you close your browser</li>
                <li className="mb-2">Persistent cookies remain on your device for a set period</li>
                <li className="mb-2">Authentication cookies typically last 30 days</li>
                <li className="mb-2">Preference cookies may last up to 1 year</li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">7. Updates to This Policy</h3>
              <p className="mb-4">We may update this Cookie Policy from time to time to reflect changes in our practices or applicable law. Any changes will be posted on this page with an updated revision date.</p>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">8. Contact Us</h3>
              <p>If you have any questions about our use of cookies, please contact us at:</p>
              <p className="mb-2">Email: cookies@callsafe.training</p>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default App;
